import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { IconButtonColumn, TableColumn, TextColumn } from '@vsolv/vectors/table';
import { SecurityService } from '@wsphere/staff/web';
import { Policy } from '@wsphere/warranties/domain';
import { combineLatest, from, map, Observable } from 'rxjs';
import { EditCancelationRuleDialog } from '../../dialogs';
import { PolicyService } from '../../services/policy.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-policy-cancelation-page',
  templateUrl: './policy-cancelation.page.html',
  standalone: false,
})
export class PolicyCancelationPage {
  constructor(
    private policySvc: PolicyService,
    private securitySvc: SecurityService,
    private toastSvc: ToastService
  ) {}

  @ViewChild('editCancelationRuleDialog') editCancelationRuleDialog!: EditCancelationRuleDialog;
  @ViewChild('deleteCancelationRuleDialog') deleteCancelationRuleDialog!: DialogComponent;

  policy$ = this.policySvc.getPolicyObservable();

  selectedPolicyCancelationRule!: Policy.CancelationRule | null;

  canView$ = from(this.securitySvc.hasAccess('pol_View', null));
  canEdit$ = from(this.securitySvc.hasAccess('pol_Edit', null));

  columns$: Observable<TableColumn<unknown>[]> = combineLatest([this.canView$, this.canEdit$]).pipe(
    map(([_, canEdit]) => {
      return [
        new TextColumn<Policy.CancelationRule>({ header: 'Jurisdiction', fitContent: true }, item => ({
          text: item.country + ' - ' + item.state,
          classes: 'font-semibold',
        })),
        new TextColumn<Policy.CancelationRule>(
          { header: 'Cancellation Fee', fitContent: true, align: 'center' },
          item => ({
            text: '$' + item.adminFee?.toFixed(2),
          })
        ),
        new TextColumn<Policy.CancelationRule>(
          { header: 'Deduct Claims', fitContent: true, align: 'center' },
          item => ({
            text: item.deductClaims ? 'Yes' : 'No',
          })
        ),
        new TextColumn<Policy.CancelationRule>({ header: 'Rule Text' }, item => ({
          text: item.ruleText ?? '',
          classes: 'max-w-[300px] truncate',
        })),
        ...(canEdit
          ? [
              new IconButtonColumn<Policy.CancelationRule>({ stickyEnd: true, fitContent: true }, item => ({
                type: 'clear',
                rounded: true,
                icon: 'edit-01',
                click: () => this.openCancelationRuleEditDialog(item),
              })),
              new IconButtonColumn<Policy.CancelationRule>({ stickyEnd: true, fitContent: true }, item => ({
                type: 'clear',
                rounded: true,
                icon: 'trash-01',
                click: () => this.openCancelationRuleDeleteDialog(item),
              })),
            ]
          : []),
      ];
    })
  );

  async deleteCancelationRule(cancelationRule: Policy.CancelationRule) {
    const deleted = await this.policySvc
      .deleteCancelationRule(cancelationRule.policyId, {
        country: cancelationRule.country,
        state: cancelationRule.state,
      })
      .catch(({ error }) => {
        this.toastSvc.show({
          type: 'error',
          title: 'Something went wrong',
          text: error.message,
        });
      });
    const deletedState = deleted && typeof deleted === 'object' && 'state' in deleted ? deleted.state : undefined;
    if (deletedState) {
      this.toastSvc.show({
        type: 'success',
        title: 'Deleted cancellation rule',
        text: 'The rule for <strong>' + deletedState + '</strong> has been deleted.',
      });
      this.policySvc.refreshPolicy();
    }
  }

  openCancelationRuleEditDialog(cancelationRule?: Policy.CancelationRule) {
    this.selectedPolicyCancelationRule = null;
    if (cancelationRule) setTimeout(() => (this.selectedPolicyCancelationRule = cancelationRule));
    this.editCancelationRuleDialog?.open();
  }

  openCancelationRuleDeleteDialog(cancelationRule?: Policy.CancelationRule) {
    this.selectedPolicyCancelationRule = null;
    if (cancelationRule) setTimeout(() => (this.selectedPolicyCancelationRule = cancelationRule));
    this.deleteCancelationRuleDialog?.open();
  }
}
